<template>
  <div class="container">
    <div class="row">
      <div class="col-xs-12 col-sm-12 col-md-12">
        <h1 class="tophead">Know your Alphabets</h1>

        <!-- Search / Filter -->
        <div class="alpha-search-wrap">
          <input
            class="alpha-search"
            type="text"
            v-model="searchQuery"
            placeholder="Search a letter, e.g. A or Apple..."
            maxlength="20"
          />
          <button class="btn btn-outline-secondary alpha-clear-btn" v-if="searchQuery" @click="searchQuery = ''">✕ Clear</button>
        </div>

        <!-- Capital Letters -->
        <div class="homebord">
          <h2 class="section-title">Capital Letters</h2>
          <div class="reveal-bar">
            <button class="btn btn-sm btn-outline-primary" @click="revealAll">Reveal All</button>
            <button class="btn btn-sm btn-outline-secondary" @click="hideAll">Hide All</button>
          </div>
          <div class="alpha-grid">
            <div
              v-for="item in filteredCapitals"
              :key="'cap-' + item.letter"
              class="alpha-card"
              :class="{ 'alpha-card--open': item.open }"
              @click="item.open = !item.open"
            >
              <div class="alpha-letter">{{ item.letter }}</div>
              <div class="alpha-phonetic">{{ item.phonetic }}</div>
              <transition name="expand">
                <div v-if="item.open" class="alpha-reveal">
                  <img :src="require('./images/' + item.image)" :alt="item.word" class="alpha-img" />
                  <span class="alpha-word">{{ item.letter }} for <strong>{{ item.word }}</strong></span>
                </div>
              </transition>
            </div>
          </div>
          <p v-if="filteredCapitals.length === 0" class="no-results">No match found for "{{ searchQuery }}".</p>
        </div>

        <!-- Small Letters -->
        <div class="homebord">
          <h2 class="section-title">Small Letters</h2>
          <div class="reveal-bar">
            <button class="btn btn-sm btn-outline-primary" @click="revealAllSmall">Reveal All</button>
            <button class="btn btn-sm btn-outline-secondary" @click="hideAllSmall">Hide All</button>
          </div>
          <div class="alpha-grid">
            <div
              v-for="item in filteredSmall"
              :key="'sm-' + item.letter"
              class="alpha-card alpha-card--small"
              :class="{ 'alpha-card--open': item.open }"
              @click="item.open = !item.open"
            >
              <div class="alpha-letter">{{ item.letter }}</div>
              <div class="alpha-phonetic">{{ item.phonetic }}</div>
              <transition name="expand">
                <div v-if="item.open" class="alpha-reveal">
                  <img :src="require('./images/' + item.image)" :alt="item.word" class="alpha-img" />
                  <span class="alpha-word">{{ item.letter }} for <strong>{{ item.word }}</strong></span>
                </div>
              </transition>
            </div>
          </div>
          <p v-if="filteredSmall.length === 0" class="no-results">No match found for "{{ searchQuery }}".</p>
        </div>

        <!-- Vowels & Consonants -->
        <div class="homebord">
          <h2 class="section-title">Vowels</h2>
          <p class="vc-desc">Vowels are letters that produce an open sound without obstruction: <strong>A, E, I, O, U</strong>.</p>
          <div class="vc-grid">
            <div v-for="v in vowels" :key="'v-' + v.letter" class="vc-card vc-card--vowel">
              <div class="vc-big">{{ v.letter }}</div>
              <div class="vc-small">{{ v.lower }}</div>
              <div class="vc-phonetic">{{ v.phonetic }}</div>
            </div>
          </div>
        </div>

        <div class="homebord">
          <h2 class="section-title">Consonants</h2>
          <p class="vc-desc">Consonants are all letters except vowels. They produce sounds that partially obstruct airflow.</p>
          <div class="vc-grid">
            <div v-for="c in consonants" :key="'c-' + c.letter" class="vc-card vc-card--consonant">
              <div class="vc-big">{{ c.letter }}</div>
              <div class="vc-small">{{ c.lower }}</div>
              <div class="vc-phonetic">{{ c.phonetic }}</div>
            </div>
          </div>
        </div>


        <!-- Alphabet Quiz -->
        <div class="homebord quiz-section">
          <h2 class="section-title">🔤 Alphabet Quiz</h2>
          <p class="quiz-subtext">Look at the image and pick the letter it starts with!</p>

          <!-- Idle -->
          <div v-if="quizState === 'idle'" class="quiz-idle">
            <div class="quiz-preview-imgs">
              <img v-for="item in quizPreview" :key="item.letter"
                :src="require('./images/' + item.image)"
                :alt="item.word" class="quiz-preview-thumb" />
            </div>
            <button class="btn btn-success quiz-start-btn" @click="startQuiz">Start Quiz</button>
          </div>

          <!-- Playing -->
          <div v-if="quizState === 'playing'" class="quiz-play">
            <div class="quiz-progress-bar-wrap">
              <div class="quiz-progress-bar" :style="{ width: quizProgressPercent + '%' }"></div>
            </div>
            <p class="quiz-counter">Question {{ quizCurrent + 1 }} of {{ quizTotal }}</p>
            <p class="quiz-score-live">Score: <strong>{{ quizScore }}</strong></p>
            <div class="quiz-img-wrap">
              <img :src="require('./images/' + quizQuestion.image)" :alt="'???'" class="quiz-main-img" />
              <p class="quiz-hint">What letter does this start with?</p>
            </div>
            <div class="quiz-options">
              <button
                v-for="opt in quizOptions"
                :key="opt"
                class="quiz-opt-btn"
                :class="getOptClass(opt)"
                :disabled="quizAnswered"
                @click="checkQuizAnswer(opt)"
              >{{ opt }}</button>
            </div>
            <transition name="fade">
              <div v-if="quizAnswered" class="quiz-feedback" :class="quizCorrect ? 'fb-correct' : 'fb-wrong'">
                <span v-if="quizCorrect">✅ Correct! <strong>{{ quizQuestion.word }}</strong> starts with <strong>{{ quizQuestion.letter }}</strong>.</span>
                <span v-else>❌ It's <strong>{{ quizQuestion.letter }}</strong> for <strong>{{ quizQuestion.word }}</strong>.</span>
                <button class="btn btn-primary quiz-next-btn" @click="nextQuizQuestion">
                  {{ quizCurrent + 1 < quizTotal ? 'Next' : 'See Results' }}
                </button>
              </div>
            </transition>
          </div>

          <!-- Results -->
          <div v-if="quizState === 'results'" class="quiz-results">
            <div class="quiz-trophy">
              <span v-if="quizScore === quizTotal">🏆</span>
              <span v-else-if="quizScore >= quizTotal * 0.75">🥈</span>
              <span v-else>🎯</span>
            </div>
            <h3>Quiz Complete!</h3>
            <p class="quiz-final-score">You scored <strong>{{ quizScore }}</strong> out of <strong>{{ quizTotal }}</strong></p>
            <p class="quiz-result-msg">{{ quizResultMsg }}</p>
            <div class="quiz-breakdown">
              <div v-for="(h, i) in quizHistory" :key="i" class="quiz-hist-item" :class="h.correct ? 'hist-correct' : 'hist-wrong'">
                <img :src="require('./images/' + h.image)" :alt="h.word" class="hist-img" />
                <span>{{ h.letter }} – {{ h.word }}</span>
                <span>{{ h.correct ? '✅' : '❌' }}</span>
              </div>
            </div>
            <button class="btn btn-success quiz-start-btn" @click="startQuiz">Play Again</button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
const ALPHA_DATA = [
  { letter: 'A', lower: 'a', word: 'Apple',     image: 'apple.jpg',     phonetic: '/eɪ/' },
  { letter: 'B', lower: 'b', word: 'Ball',       image: 'football.jpg',  phonetic: '/biː/' },
  { letter: 'C', lower: 'c', word: 'Cat',        image: 'cat.jpeg',      phonetic: '/siː/' },
  { letter: 'D', lower: 'd', word: 'Dog',        image: 'dog.jpeg',      phonetic: '/diː/' },
  { letter: 'E', lower: 'e', word: 'Egg',        image: 'egg.jpeg',      phonetic: '/iː/' },
  { letter: 'F', lower: 'f', word: 'Fish',       image: 'fish.jpeg',     phonetic: '/ɛf/' },
  { letter: 'G', lower: 'g', word: 'Goat',       image: 'goat.jpeg',     phonetic: '/dʒiː/' },
  { letter: 'H', lower: 'h', word: 'House',      image: 'house.jpeg',    phonetic: '/eɪtʃ/' },
  { letter: 'I', lower: 'i', word: 'Iron',       image: 'iron.jpeg',     phonetic: '/aɪ/' },
  { letter: 'J', lower: 'j', word: 'Jug',        image: 'jug.jpeg',      phonetic: '/dʒeɪ/' },
  { letter: 'K', lower: 'k', word: 'Kite',       image: 'kite.jpeg',     phonetic: '/keɪ/' },
  { letter: 'L', lower: 'l', word: 'Lion',       image: 'lion.jpeg',     phonetic: '/ɛl/' },
  { letter: 'M', lower: 'm', word: 'Mat',        image: 'mat.jpeg',      phonetic: '/ɛm/' },
  { letter: 'N', lower: 'n', word: 'Nose',       image: 'nose.jpeg',     phonetic: '/ɛn/' },
  { letter: 'O', lower: 'o', word: 'Orange',     image: 'orange.jpeg',   phonetic: '/oʊ/' },
  { letter: 'P', lower: 'p', word: 'Pencil',     image: 'pencil.jpeg',   phonetic: '/piː/' },
  { letter: 'Q', lower: 'q', word: 'Queen',      image: 'queen.jpeg',    phonetic: '/kjuː/' },
  { letter: 'R', lower: 'r', word: 'Rat',        image: 'rat.jpeg',      phonetic: '/ɑːr/' },
  { letter: 'S', lower: 's', word: 'Ship',       image: 'ship.jpeg',     phonetic: '/ɛs/' },
  { letter: 'T', lower: 't', word: 'Table',      image: 'table.png',     phonetic: '/tiː/' },
  { letter: 'U', lower: 'u', word: 'Umbrella',   image: 'umbrella.jpeg', phonetic: '/juː/' },
  { letter: 'V', lower: 'v', word: 'Van',        image: 'van.jpeg',      phonetic: '/viː/' },
  { letter: 'W', lower: 'w', word: 'Wheel',      image: 'wheel.jpeg',    phonetic: '/ˈdʌbəljuː/' },
  { letter: 'X', lower: 'x', word: 'Xylophone',  image: 'xylophone.jpeg',phonetic: '/ɛks/' },
  { letter: 'Y', lower: 'y', word: 'Yam',        image: 'yam.jpeg',      phonetic: '/waɪ/' },
  { letter: 'Z', lower: 'z', word: 'Zebra',      image: 'zebra.jpeg',    phonetic: '/zɛd/' },
];

const VOWEL_LETTERS = ['A', 'E', 'I', 'O', 'U'];

export default {
  data() {
    const capitals = ALPHA_DATA.map(d => ({ ...d, open: false }));
    const small    = ALPHA_DATA.map(d => ({ ...d, letter: d.lower, open: false }));
    return {
      searchQuery: '',
      capitals,
      small,
      vowels:     ALPHA_DATA.filter(d => VOWEL_LETTERS.includes(d.letter)),
      consonants: ALPHA_DATA.filter(d => !VOWEL_LETTERS.includes(d.letter)),

      // Quiz
      quizState:    'idle',
      quizTotal:    10,
      quizCurrent:  0,
      quizScore:    0,
      quizQueue:    [],
      quizQuestion: null,
      quizOptions:  [],
      quizAnswered: false,
      quizCorrect:  false,
      quizSelected: null,
      quizHistory:  [],
    };
  },
  computed: {
    filteredCapitals() {
      const q = this.searchQuery.trim().toUpperCase();
      if (!q) return this.capitals;
      return this.capitals.filter(
        d => d.letter.toUpperCase().includes(q) || d.word.toUpperCase().includes(q)
      );
    },
    filteredSmall() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.small;
      return this.small.filter(
        d => d.letter.toLowerCase().includes(q) || d.word.toLowerCase().includes(q)
      );
    },
    quizProgressPercent() {
      return (this.quizCurrent / this.quizTotal) * 100;
    },
    quizResultMsg() {
      const pct = this.quizScore / this.quizTotal;
      if (pct === 1)   return "Perfect! You know every letter! 🌟";
      if (pct >= 0.75) return "Great job! Almost there!";
      if (pct >= 0.5)  return "Not bad - keep practising!";
      return "Keep learning - you'll get there!";
    },
    quizPreview() {
      return ALPHA_DATA.slice(0, 5);
    },
  },
  methods: {
    revealAll()    { this.capitals.forEach(d => (d.open = true));  },
    hideAll()      { this.capitals.forEach(d => (d.open = false)); },
    revealAllSmall(){ this.small.forEach(d => (d.open = true));    },
    hideAllSmall() { this.small.forEach(d => (d.open = false));    },

    shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); },

    startQuiz() {
      this.quizScore    = 0;
      this.quizCurrent  = 0;
      this.quizHistory  = [];
      this.quizAnswered = false;
      this.quizSelected = null;
      this.quizQueue    = this.shuffle(ALPHA_DATA).slice(0, this.quizTotal);
      this.quizState    = 'playing';
      this.loadQuizQ();
    },
    loadQuizQ() {
      this.quizAnswered = false;
      this.quizSelected = null;
      this.quizQuestion = this.quizQueue[this.quizCurrent];
      const others = ALPHA_DATA.filter(d => d.letter !== this.quizQuestion.letter);
      const wrongs = this.shuffle(others).slice(0, 3).map(d => d.letter);
      this.quizOptions  = this.shuffle([this.quizQuestion.letter, ...wrongs]);
    },
    checkQuizAnswer(opt) {
      this.quizAnswered = true;
      this.quizSelected = opt;
      this.quizCorrect  = opt === this.quizQuestion.letter;
      if (this.quizCorrect) this.quizScore++;
      this.quizHistory.push({
        letter: this.quizQuestion.letter,
        word:   this.quizQuestion.word,
        image:  this.quizQuestion.image,
        correct: this.quizCorrect,
      });
    },
    nextQuizQuestion() {
      if (this.quizCurrent + 1 < this.quizTotal) {
        this.quizCurrent++;
        this.loadQuizQ();
      } else {
        this.quizState = 'results';
      }
    },
    getOptClass(opt) {
      if (!this.quizAnswered) return '';
      if (opt === this.quizQuestion.letter) return 'opt-correct';
      if (opt === this.quizSelected)        return 'opt-wrong';
      return '';
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }

.tophead { text-align: center; margin: 16px 0 10px; font-size: 2rem; font-weight: 700; }

/* Search */
.alpha-search-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
}
.alpha-search {
  flex: 1;
  max-width: 420px;
  padding: 10px 16px;
  font-size: 1rem;
  border: 2px solid #aaa;
  border-radius: 30px;
  outline: none;
  transition: border-color 0.2s;
}
.alpha-search:focus { border-color: #1e88e5; }
.alpha-clear-btn { border-radius: 20px; }

/* Section containers */
.homebord {
  border: 2px solid black;
  margin-bottom: 30px;
  margin-top: 25px;
  border-radius: 15px;
  padding: 20px 20px 24px;
}
.section-title {
  text-align: center;
  text-decoration: underline;
  font-weight: bolder;
  margin-bottom: 10px;
  font-size: 1.5rem;
}
.reveal-bar {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
}

/* Alphabet grid */
.alpha-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: center;
}
.alpha-card {
  width: 130px;
  min-height: 100px;
  background: #a9f7ea;
  border-radius: 14px;
  border: 2px solid transparent;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px 10px;
  transition: background 0.2s, border-color 0.2s, transform 0.15s;
  user-select: none;
}
.alpha-card:hover { background: #6ef0da; transform: translateY(-3px); }
.alpha-card--open { background: #123b37; border-color: #123b37; color: #fff; }
.alpha-card--open:hover { background: #1a5248; }
.alpha-card--small .alpha-letter { color: #1a6b5e; }
.alpha-card--open .alpha-letter { color: #b1f8f1; }

.alpha-letter {
  font-size: 2.2rem;
  font-weight: 800;
  line-height: 1;
}
.alpha-phonetic {
  font-size: 0.72rem;
  color: #555;
  margin: 3px 0 6px;
  font-style: italic;
}
.alpha-card--open .alpha-phonetic { color: #a0e8e0; }

.alpha-reveal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.alpha-img {
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 10px;
  border: 2px solid rgba(255,255,255,0.4);
  background: #fff;
  padding: 4px;
}
.alpha-word { font-size: 0.8rem; text-align: center; }

.no-results { text-align: center; color: #888; font-style: italic; margin-top: 10px; }

/* Expand transition */
.expand-enter-active, .expand-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.expand-enter, .expand-leave-to { opacity: 0; transform: scaleY(0.8); }

/* Vowels & Consonants */
.vc-desc { text-align: center; font-size: 1rem; color: #444; margin-bottom: 16px; }
.vc-grid { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; }
.vc-card {
  width: 80px;
  padding: 14px 8px;
  border-radius: 14px;
  text-align: center;
  border: 2px solid transparent;
  transition: transform 0.15s;
}
.vc-card:hover { transform: translateY(-3px); }
.vc-card--vowel    { background: #fff3e0; border-color: #ffb300; }
.vc-card--consonant{ background: #e3f2fd; border-color: #1e88e5; }
.vc-big   { font-size: 1.8rem; font-weight: 800; }
.vc-small { font-size: 1.1rem; color: #666; }
.vc-phonetic { font-size: 0.68rem; color: #888; font-style: italic; margin-top: 4px; }

/* Quiz */
.quiz-section { }
.quiz-subtext { text-align: center; font-size: 1.05rem; color: #555; margin-bottom: 20px; }

.quiz-idle { display: flex; flex-direction: column; align-items: center; gap: 18px; }
.quiz-preview-imgs { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
.quiz-preview-thumb {
  width: 70px;
  height: 70px;
  object-fit: contain;
  border-radius: 10px;
  border: 2px solid #ccc;
  background: #fff;
  padding: 4px;
}
.quiz-start-btn { font-size: 1.1rem; padding: 10px 40px; border-radius: 30px; }

.quiz-play { display: flex; flex-direction: column; align-items: center; }
.quiz-progress-bar-wrap {
  width: 100%; max-width: 500px; height: 10px;
  background: #e0e0e0; border-radius: 10px;
  margin: 0 auto 14px; overflow: hidden;
}
.quiz-progress-bar { height: 100%; background: #43a047; border-radius: 10px; transition: width 0.4s ease; }
.quiz-counter { color: #777; font-size: 0.95rem; margin-bottom: 4px; }
.quiz-score-live { font-size: 1.05rem; margin-bottom: 16px; }

.quiz-img-wrap { text-align: center; margin-bottom: 20px; }
.quiz-main-img {
  width: 160px;
  height: 160px;
  object-fit: contain;
  border-radius: 18px;
  border: 3px solid #ccc;
  background: #fff;
  padding: 10px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}
.quiz-hint { font-size: 1rem; color: #555; margin-top: 10px; }

.quiz-options { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; margin-bottom: 20px; }
.quiz-opt-btn {
  min-width: 70px; padding: 12px 20px;
  font-size: 1.3rem; font-weight: 700;
  border: 2px solid #888; border-radius: 10px;
  background: #fff; cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.1s;
}
.quiz-opt-btn:not(:disabled):hover { background: #f0f0f0; transform: translateY(-2px); }
.quiz-opt-btn:disabled { cursor: default; }
.opt-correct { background: #c8e6c9 !important; border-color: #388e3c !important; color: #1b5e20; }
.opt-wrong   { background: #ffcdd2 !important; border-color: #c62828 !important; color: #b71c1c; }

.quiz-feedback {
  padding: 14px 22px; border-radius: 12px; font-size: 1rem;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  max-width: 420px; width: 100%;
}
.fb-correct { background: #e8f5e9; border: 2px solid #66bb6a; color: #2e7d32; }
.fb-wrong   { background: #ffebee; border: 2px solid #ef9a9a; color: #c62828; }
.quiz-next-btn { padding: 8px 28px; border-radius: 20px; }

.quiz-results { display: flex; flex-direction: column; align-items: center; gap: 12px; }
.quiz-trophy { font-size: 4rem; line-height: 1; }
.quiz-final-score { font-size: 1.25rem; }
.quiz-result-msg { font-size: 1rem; color: #444; font-style: italic; }

.quiz-breakdown { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin: 10px 0; max-width: 600px; }
.quiz-hist-item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; border-radius: 10px; font-size: 0.9rem; font-weight: 600;
}
.hist-correct { background: #e8f5e9; border: 1px solid #a5d6a7; }
.hist-wrong   { background: #ffebee; border: 1px solid #ef9a9a; }
.hist-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  padding: 2px;
}

/* Fade */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* Responsive */
@media (max-width: 600px) {
  .alpha-card { width: 90px; min-height: 80px; padding: 8px 4px; }
  .alpha-letter { font-size: 1.7rem; }
  .alpha-img { width: 58px; height: 58px; }
  .quiz-preview-thumb { width: 60px; height: 60px; }
  .quiz-main-img { width: 140px; height: 140px; }
  .hist-img { width: 32px; height: 32px; }
  .vc-card { width: 64px; }
}
@media (max-width: 768px) {
  .alpha-card { width: 105px; }
}
</style>
